import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Input,
  FormFeedback,
  Alert,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { updateLead, getLeadById } from '../../../../redux/actions/lead';
import { useVisitValidation } from './formik';
import { PAYMENT_METHOD_OPTIONS } from '../../constants';
import { formatFormCurrency } from '../../../../utils/utils';

function VisitForm({ leadId, leadOrder, onSubmit, onCancel }) {
  const dispatch = useDispatch();
  const updateState = useSelector((state) => state.lead.update);
  const leadDetailState = useSelector((state) => state.lead.getById);
  const [modal, setModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const formik = useVisitValidation((values) => {
    setLoading(true);
    setErrorMessage('');

    if (!leadId) {
      setErrorMessage('No se puede procesar: ID de lead no válido.');
      setLoading(false);
      return;
    }

    const leadInfo = {
      state: 'visit',
      visitDatetime: values.datetime,
      visitIsArrived: values.isArrived,
      visitPrice: values.price.toString().replace(/\./g, ''),
      visitIva: values.iva,
      visitPaymentMethod: values.paymentMethod,
      visitObservation: values.observation,
      order: leadOrder,
    };

    dispatch(updateLead(leadId, leadInfo));
  });

  const toggle = (operation) => {
    setModal(!modal);
    if (operation === 'submit') {
      onSubmit();
    } else {
      onCancel();
    }
  };

  useEffect(() => {
    if (!formik.values.isArrived) {
      formik.setFieldValue('price', '', false);
      formik.setFieldValue('iva', '', false);
      formik.setFieldValue('paymentMethod', '', false);
      formik.setFieldValue('observation', '', false);
    }
  }, [formik.values.isArrived]);

  useEffect(() => {
    if (updateState) {
      setLoading(false);

      if (updateState.status === 'success') {
        toggle('submit');
      } else if (updateState.status === 'danger') {
        setErrorMessage(updateState.message || 'Error al agendar la visita');
      }
    }
  }, [updateState]);

  useEffect(() => {
    if (leadId) {
      setLoading(true);
      setErrorMessage(null);
      dispatch(getLeadById(leadId));
    } else {
      setErrorMessage('No se proporcionó un ID de lead válido');
      setLoading(false);
    }
  }, [leadId, dispatch]);

  useEffect(() => {
    if (leadDetailState) {
      setLoading(false);
      if (leadDetailState.status === 'danger') {
        setErrorMessage(
          leadDetailState.message || 'Error al cargar los detalles del lead',
        );
      } else {
        const visitObj = leadDetailState.data.data.visit;
        if (visitObj) {
          formik.setFieldValue('datetime', visitObj.datetime);
          formik.setFieldValue('isArrived', visitObj.isArrived);
          if (visitObj.offerId) {
            const formatted = formatFormCurrency(visitObj.price);
            formik.setFieldValue('price', formatted);
            if (visitObj.iva === '+ IVA') {
              formik.setFieldValue('iva', 'Mas IVA');
            } else {
              formik.setFieldValue('iva', visitObj.iva);
            }
            formik.setFieldValue('paymentMethod', visitObj.paymentMethod);
            formik.setFieldValue('observation', visitObj.observation);
            setTimeout(() => {
              formik.setTouched({
                datetime: true,
                isArrived: true,
                price: true,
                iva: true,
                paymentMethod: true,
                observation: true,
              });
            }, 100);
          }
        }
      }
    }
  }, [leadDetailState]);

  return (
    <Modal isOpen={modal} toggle={() => toggle('cancel')}>
      <ModalHeader toggle={() => toggle('cancel')}>Agendar Visita</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          {errorMessage && (
            <Alert color="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}

          <p>
            Programa una visita para este cliente. Asegúrate de especificar la
            fecha y hora de la visita.
          </p>

          <section className="mb-3">
            <Label for="datetime">Fecha y hora de visita</Label>
            <Input
              type="datetime-local"
              name="datetime"
              id="datetime"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.datetime}
              valid={formik.touched.datetime && !formik.errors.datetime}
              invalid={formik.touched.datetime && !!formik.errors.datetime}
            />
            <FormFeedback>{formik.errors.datetime}</FormFeedback>
          </section>

          <section className="mb-3">
            <Label for="isArrived" className="mr-5">
              Si el cliente asistió a la visita y presentó una oferta, marcar el
              checkbox
            </Label>
            <Input
              type="checkbox"
              name="isArrived"
              id="isArrived"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              checked={formik.values.isArrived}
            />
          </section>

          {formik.values.isArrived && (
            <>
              <section className="mb-3">
                <Label for="price">Precio</Label>
                <Input
                  type="text"
                  name="price"
                  id="price"
                  placeholder="Ingresa el precio"
                  onChange={(e) => {
                    const formatted = formatFormCurrency(e.target.value);
                    formik.setFieldValue('price', formatted);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.price}
                  valid={formik.touched.price && !formik.errors.price}
                  invalid={formik.touched.price && !!formik.errors.price}
                />
                <FormFeedback>{formik.errors.price}</FormFeedback>
              </section>

              <section className="mb-3">
                <Label>IVA</Label>
                <div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      name="iva"
                      id="ivaMas"
                      value="Mas IVA"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.iva === 'Mas IVA'}
                      className="form-check-input ml-2"
                    />
                    <Label className="form-check-label" for="ivaMas">
                      Más IVA
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      name="iva"
                      id="ivaCon"
                      value="Con IVA"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.iva === 'Con IVA'}
                      className="form-check-input ml-2"
                    />
                    <Label className="form-check-label" for="ivaCon">
                      Con IVA
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      name="iva"
                      id="ivaExento"
                      value="Exento de IVA"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.iva === 'Exento de IVA'}
                      className="form-check-input ml-2"
                    />
                    <Label className="form-check-label" for="ivaExento">
                      Exento de IVA
                    </Label>
                  </div>
                </div>
                {formik.touched.iva && formik.errors.iva && (
                  <div className="text-danger">{formik.errors.iva}</div>
                )}
              </section>

              <section className="mb-3">
                <Label for="paymentMethod">Forma de pago</Label>
                <Input
                  type="select"
                  name="paymentMethod"
                  id="paymentMethod"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.paymentMethod}
                  valid={
                    formik.touched.paymentMethod && !formik.errors.paymentMethod
                  }
                  invalid={
                    formik.touched.paymentMethod &&
                    !!formik.errors.paymentMethod
                  }
                >
                  <option value="">Selecciona una opción</option>
                  {PAYMENT_METHOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.paymentMethod}</FormFeedback>
              </section>

              <section className="mb-3">
                <Label for="observation">Observación</Label>
                <Input
                  type="textarea"
                  name="observation"
                  id="observation"
                  placeholder="Ingresa cualquier observación relevante"
                  className="p-2"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.observation}
                  valid={
                    formik.touched.observation && !formik.errors.observation
                  }
                  invalid={
                    formik.touched.observation && !!formik.errors.observation
                  }
                />
                <FormFeedback>{formik.errors.observation}</FormFeedback>
              </section>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            type="submit"
            color="primary"
            disabled={!formik.isValid || !formik.dirty || loading}
          >
            {loading ? 'Agendando...' : 'Agendar visita'}
          </Button>{' '}
          <Button color="secondary" onClick={() => toggle('cancel')}>
            Cancelar
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default VisitForm;
